import {
  Alert,
  Button,
  Card,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import {
  areDatesClose,
  formatReadableDate,
} from "portal/utils/categoryCollection/plantProfile";
import { classes } from "portal/theme/theme";
import { SavedVersionMetadata } from "protos/portal/category_profile";
import { ShootTimeExpandButton } from "../ShootTimeExpandButton";
import { titleCase } from "common/utils/strings";
import { useTranslation } from "react-i18next";
import { useVersionHistory } from "./VersionHistoryProvider";
import { VersionUser } from "./VersionUser";
import BranchIcon from "@mui/icons-material/CallSplit";
import React, { FC } from "react";
import RevertIcon from "@mui/icons-material/Undo";

export interface VersionHeaderProps {
  metadata: SavedVersionMetadata;
  onRevert?: (revertToVersionId: string) => Promise<unknown>;
  isReverting?: boolean;
  onlyChanges: boolean;
  onToggleOnlyChanges: (onlyChanges: boolean) => void;
  hasShootTime: boolean;
  shootTimeExpanded: boolean;
  onToggleShootTime: () => void;
}

export const VersionHeader: FC<VersionHeaderProps> = ({
  metadata: {
    user,
    clientVersionCreateTime,
    activeTime,
    versionId,
    parentVersionId,
    revertVersionId,
  },
  onRevert,
  isReverting = false,
  onlyChanges,
  onToggleOnlyChanges,
  hasShootTime,
  shootTimeExpanded,
  onToggleShootTime,
}) => {
  const { t, i18n } = useTranslation();
  const {
    setSelectedVersionId,
    currentVersionId,
    refetchVersions,
    onShowParent,
    onShowRevert,
  } = useVersionHistory();
  return (
    <Card
      className="flex flex-col p-2"
      style={{ backgroundColor: activeTime ? "transparent" : undefined }}
      variant="outlined"
    >
      {!activeTime && (
        <Alert className="mb-2" severity="warning">
          {t(
            "components.categoryCollectionProfile.history.neverActivatedVersion"
          )}
        </Alert>
      )}
      <div className="flex justify-between flex-wrap gap-2">
        <div className="flex gap-4 items-center flex-wrap">
          {activeTime && (
            <Typography variant="body2" className="text-xs">
              {activeTime && formatReadableDate(activeTime, i18n)}
            </Typography>
          )}
          {user && <VersionUser user={user} />}
          {clientVersionCreateTime &&
          (!activeTime ||
            (activeTime &&
              areDatesClose(clientVersionCreateTime, activeTime))) ? (
            <Typography
              color="textSecondary"
              variant="body2"
              className="text-xs"
            >
              {t("components.categoryCollectionProfile.history.recorded", {
                clientVersionCreateTime: formatReadableDate(
                  clientVersionCreateTime,
                  i18n
                ),
              })}
            </Typography>
          ) : undefined}
        </div>
        <div className="flex items-center gap-4 justify-center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                onClick={() => {
                  onToggleOnlyChanges(!onlyChanges);
                }}
                checked={onlyChanges}
              />
            }
            label={
              <Typography variant="caption">
                {t(
                  "components.categoryCollectionProfile.history.actions.onlyChanges"
                )}
              </Typography>
            }
          />
          {hasShootTime && (
            <ShootTimeExpandButton
              expanded={shootTimeExpanded}
              onToggle={onToggleShootTime}
            />
          )}
        </div>
        <div
          className={classes("flex items-center gap-2 justify-end", {
            "min-w-1/4": currentVersionId === versionId,
          })}
        >
          {currentVersionId !== versionId && onRevert && (
            <Button
              size="small"
              variant="contained"
              startIcon={<RevertIcon />}
              onClick={async () => {
                await onRevert(versionId);
                refetchVersions();
              }}
              disabled={isReverting}
            >
              {t("components.categoryCollectionProfile.history.actions.revert")}
            </Button>
          )}
        </div>
      </div>
      <div className="flex gap-2 items-center justify-center flex-wrap">
        {parentVersionId && (
          <Button
            variant="text"
            color="secondary"
            className="normal-case"
            startIcon={<BranchIcon />}
            onClick={() => {
              onShowParent(versionId, parentVersionId);
              setSelectedVersionId(parentVersionId);
            }}
          >
            {titleCase(
              t(
                "components.categoryCollectionProfile.history.actions.seeParent"
              )
            )}
          </Button>
        )}
        {revertVersionId && (
          <Button
            variant="text"
            color="error"
            className="normal-case"
            startIcon={<RevertIcon />}
            onClick={() => {
              onShowRevert(versionId, revertVersionId);
              setSelectedVersionId(revertVersionId);
            }}
          >
            {titleCase(
              t(
                "components.categoryCollectionProfile.history.actions.seeRevert"
              )
            )}
          </Button>
        )}
      </div>
    </Card>
  );
};
